<script lang="ts">
  import type { SurfaceProject, SurfaceTask } from "./types";
  import { fileGtTask } from "./api";
  import { loadSnapshot } from "./store";

  let {
    task,
    projects,
    onclose,
    onerror,
  }: {
    task: SurfaceTask;
    projects: SurfaceProject[];
    onclose: () => void;
    onerror?: (msg: string) => void;
  } = $props();

  let projectSlug = $state<string>("");
  let saving = $state(false);
  let error = $state("");

  function rank(p: SurfaceProject): number {
    return p.priority === "high" ? 0 : p.priority === "medium" ? 1 : 2;
  }
  const projectOptions = $derived(
    [...projects]
      .filter((p) => p.status === "active")
      .sort((a, b) => rank(a) - rank(b) || a.title.localeCompare(b.title)),
  );

  $effect(() => {
    if (!projectSlug && projectOptions.length > 0) {
      projectSlug = projectOptions[0].slug;
    }
  });

  async function submit(e: Event) {
    e.preventDefault();
    if (!projectSlug || saving) return;
    if (!task.account || !task.gt_id) {
      error = "task is missing gt account/id";
      return;
    }
    saving = true;
    error = "";
    try {
      await fileGtTask({
        gt_account: task.account,
        gt_id: task.gt_id,
        project_slug: projectSlug,
        section: "Tasks",
      });
      await loadSnapshot({ fresh: true });
      onclose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      error = msg;
      onerror?.(msg);
    } finally {
      saving = false;
    }
  }

  function backdropClick(e: MouseEvent) {
    if (e.target === e.currentTarget) onclose();
  }
</script>

<div
  class="backdrop"
  role="presentation"
  onclick={backdropClick}
  onkeydown={(e) => e.key === "Escape" && onclose()}
>
  <div class="sheet" role="dialog" aria-label="File task into project">
    <div class="grabber"></div>

    <div class="sheet-head">
      <button type="button" class="cancel" onclick={onclose}>Cancel</button>
      <h2 class="sheet-title">File Task</h2>
      <button
        type="button"
        class="done"
        disabled={!projectSlug || saving}
        onclick={(e) => submit(e as unknown as Event)}
      >
        {saving ? "Filing…" : "File"}
      </button>
    </div>

    <p class="task-text">{task.text}</p>

    <form onsubmit={submit}>
      <div class="grouped-fields">
        <label class="field">
          <span class="field-label">Project</span>
          <select bind:value={projectSlug}>
            {#each projectOptions as p (p.slug)}
              <option value={p.slug}>
                {p.priority === "high" ? "🔥 " : ""}{p.title}
              </option>
            {/each}
          </select>
        </label>
      </div>

      {#if error}
        <p class="error">{error}</p>
      {/if}
    </form>
  </div>
</div>

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    background: var(--material-overlay);
    -webkit-backdrop-filter: blur(4px);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    z-index: 100;
    animation: fadeIn 0.18s ease-out;
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  .sheet {
    background: var(--bg-elev);
    border-top-left-radius: 14px;
    border-top-right-radius: 14px;
    width: 100%;
    max-width: 540px;
    padding: 6px 16px calc(20px + var(--safe-bottom));
    animation: slideUp 0.24s cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  @keyframes slideUp {
    from { transform: translateY(100%); }
    to   { transform: translateY(0); }
  }
  .grabber {
    width: 36px;
    height: 5px;
    background: rgba(235, 235, 245, 0.25);
    border-radius: 999px;
    margin: 6px auto 6px;
  }

  .sheet-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 0 12px;
    gap: 12px;
  }
  .sheet-title {
    margin: 0;
    font-size: 17px;
    font-weight: 600;
    letter-spacing: -0.005em;
  }
  .cancel { color: var(--accent); font-size: 17px; padding: 6px 4px; }
  .done { color: var(--accent); font-weight: 600; font-size: 17px; padding: 6px 4px; }
  .done:disabled { opacity: 0.4; }

  .task-text {
    color: var(--fg-dim);
    font-size: 15px;
    margin: 0 0 14px;
    line-height: 1.35;
  }

  form { display: flex; flex-direction: column; gap: 14px; }

  .grouped-fields {
    background: var(--bg-elev-2);
    border-radius: 10px;
    overflow: hidden;
  }
  .field {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
  }
  .field-label {
    font-size: 15px;
    color: var(--fg);
    flex-shrink: 0;
    min-width: 80px;
  }
  .field select {
    flex: 1;
    background: transparent;
    border: 0;
    padding: 0;
    text-align: right;
    color: var(--fg-dim);
    font-size: 15px;
    appearance: none;
  }

  .error { color: var(--priority-high); font-size: 13px; margin: 0; }
</style>
